// Menselijk tempo voor de chatwidget. Een antwoord dat in een halve seconde
// compleet verschijnt, of woord voor woord binnenstroomt, voelt als een
// machine. Een medewerker leest eerst, denkt even na en typt dan; hoe langer
// het antwoord, hoe langer het typen duurt. De tijden variëren per bericht.

export type Random = () => number;

const between = (random: Random, min: number, max: number) => min + random() * (max - min);

/**
 * Totale tijd (ms) vanaf het versturen van de vraag tot het antwoord in beeld
 * komt: lezen en nadenken, plus typen naar lengte van het antwoord.
 * Soms (ongeveer één op de zes) duurt nadenken duidelijk langer.
 */
export function humanReplyDelayMs(reply: string, random: Random = Math.random): number {
  const length = reply.trim().length;
  const think = random() < 0.16 ? between(random, 5_000, 9_000) : between(random, 1_200, 3_200);
  const charsPerSecond = between(random, 14, 22);
  const typing = Math.min(8_000, Math.max(800, (length / charsPerSecond) * 1000));
  return Math.round(think + typing);
}

/** Hoe lang er nog gewacht moet worden, gegeven de tijd die al verstreken is. */
export function remainingDelayMs(targetMs: number, startedAt: number, now: number): number {
  return Math.max(0, Math.round(targetMs - (now - startedAt)));
}

/**
 * Typtijd voor een bericht dat via de livechat binnenkomt (Twilper of een
 * medewerker). Het bericht is al geschreven, dus alleen een korte typfase
 * zodat het niet uit het niets verschijnt.
 */
export function liveMessageTypingMs(content: string, random: Random = Math.random): number {
  const length = content.trim().length;
  return Math.round(Math.min(4_000, Math.max(900, (length / between(random, 25, 35)) * 1000)));
}

/** Ouder dan dit is een livebericht al "gebeurd": meteen op zijn plek, zonder typfase. */
export const LIVE_MESSAGE_STALE_MS = 60_000;

/**
 * Zet een bericht op volgorde van tijd: vóór het eerste bericht dat later is.
 * Een teambericht van uren geleden dat pas bij de eerste poll binnenkomt, komt
 * zo niet onder het nieuwste antwoord te staan (17-10-2026: "Goedemiddag").
 */
export function insertChronologically<T extends { at?: number }>(messages: T[], message: T): T[] {
  if (message.at === undefined) return [...messages, message];
  const index = messages.findIndex((item) => item.at !== undefined && item.at > (message.at as number));
  if (index === -1) return [...messages, message];
  return [...messages.slice(0, index), message, ...messages.slice(index)];
}
